import type { Client, PickerResponse } from 'filestack-js';
import type { PickerFileMetadata } from 'filestack-js/build/main/lib/picker';
import { addAction, addSuppressedError } from '../logger';

/**
 * Maximum total pixels allowed for image uploads (100 megapixels).
 * Matches the media_service maxInputPixels hard limit — images above
 * this threshold cause 500s during server-side processing.
 */
export const MAX_UPLOAD_PIXELS = 100_000_000;

/**
 * Determines whether an already-stored image exceeds {@link MAX_UPLOAD_PIXELS}.
 * Skips non-image files (e.g. video).
 *
 * Dimensions come from Filestack's server-side {@link Client.metadata} (read
 * from the file header) rather than decoding the image in the browser — the
 * browser refuses to decode very large images, which is exactly the case we
 * need to catch. This runs for every source (local and remote) so the oversized
 * handling is uniform.
 *
 * Returns `false` when metadata can't be read so a transient failure never
 * blocks a legitimate upload. The error is reported via {@link addSuppressedError}
 * rather than swallowed silently, so a persistent metadata outage — which would
 * quietly disable the 100MP guard — is observable.
 */
export const isOversizedImage = async (client: Client | undefined, file: PickerFileMetadata): Promise<boolean> => {
  if (!client || !file.mimetype?.startsWith('image/')) {
    return false;
  }

  // The metadata round-trip gates the consumer's onUploadDone, so its latency
  // is user-visible; record it per call so slow-guard regressions are measurable.
  const startedAt = Date.now();
  try {
    const { width, height } = await client.metadata(file.handle, { width: true, height: true });
    const oversized = typeof width === 'number' && typeof height === 'number' && width * height > MAX_UPLOAD_PIXELS;
    addAction('filestackPixelGuard', { durationMs: Date.now() - startedAt, source: file.source, oversized });
    return oversized;
  } catch (error) {
    addSuppressedError(error, { context: 'filestack#isOversizedImage', handle: file.handle, source: file.source, durationMs: Date.now() - startedAt });
    return false;
  }
};

export type ProcessedUpload = Readonly<{
  /** The picker response to forward to the consumer, with oversized images removed. */
  result: PickerResponse;
  /** Whether any oversized image was dropped — the caller shows a single "too large" toast. */
  hasOversized: boolean;
  /**
   * Whether there is anything left worth forwarding. `false` when every uploaded
   * file was oversized and there were no genuine failures, so the caller can skip
   * `onUploadDone` entirely (the user has already been notified via the toast).
   */
  shouldForward: boolean;
}>;

/**
 * Validates a completed upload before it is forwarded to the consumer. This is
 * the single entry point for upload-level validation — today it audits image
 * dimensions against the pixel cap (the first such check); future validations
 * should be added here.
 *
 * Oversized images are filtered out of the forwarded response so they never
 * reach the downstream media_service path; genuine Filestack failures are passed
 * through untouched.
 */
export const processUploadedFiles = async (client: Client | undefined, files: PickerResponse): Promise<ProcessedUpload> => {
  const oversizedFlags = await Promise.all(files.filesUploaded.map(file => isOversizedImage(client, file)));
  const filesUploaded = files.filesUploaded.filter((_, index) => !oversizedFlags[index]);
  const hasOversized = filesUploaded.length !== files.filesUploaded.length;
  const result: PickerResponse = { filesUploaded, filesFailed: files.filesFailed };
  const shouldForward = !(hasOversized && filesUploaded.length === 0 && files.filesFailed.length === 0);

  return { result, hasOversized, shouldForward };
};
